import React, { useState, useEffect, useCallback } from "react";
import {
  Input,
  Button,
  Popconfirm,
  Typography,
  message,
  Drawer,
  Tag,
  Tooltip,
  Modal,
  Switch,
  Spin,
  Image,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  IdcardOutlined,
  ShoppingOutlined,
  AppstoreOutlined,
  TagOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import BrandsServ from "../../service/BrandsServ";
import ProductServ from "../../service/ProductServ";
import AddBrands from "../../component/AddBrands";
import { formatDate } from "../../util/dateUtils";
import "../../css/brand.css";
import Breadcrumbs from "../../component/Breadcrumbs";
import EmptyState from "../../util/EmptyState";
import { ResetButton } from "../../component/ui/ActionButtons";
import DataTable from "../../component/ui/DataTable";
import BrandTableImage from "../../component/ui/BrandTableImage";
import SafeImage from "../../component/SafeImage";
import { useAdminTable } from "../hooks/useAdminTable";

const ProductBrands = () => {
  const [addBrands, setAddBrands] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [brandDetails, setBrandDetails] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [descExpanded, setDescExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Drawer states for products
  const [productDrawerOpen, setProductDrawerOpen] = useState(false);
  const [selectedBrandProducts, setSelectedBrandProducts] = useState([]);
  const [selectedBrandName, setSelectedBrandName] = useState("");

  // Product full details modal states
  const [selectedProductForDetails, setSelectedProductForDetails] = useState(null);
  const [productDetailsLoading, setProductDetailsLoading] = useState(false);
  const [detailImageError, setDetailImageError] = useState(false);

  const formatPrice = (price) => {
    return price !== undefined && price !== null ? `₹${Number(price).toLocaleString('en-IN')}` : "₹0";
  };

  const getImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http") || url.startsWith("https") || url.startsWith("data:")) return url;
    return `${process.env.REACT_APP_API_URL || "http://localhost:5000"}${url}`;
  };

  const handleProductCardClick = async (product) => {
    const productId = product._id || product.id || product.productId;
    if (!productId) return;
    try {
      setProductDetailsLoading(true);
      setDetailImageError(false);
      // Pre-fill with available drawer details
      setSelectedProductForDetails(product);
      const res = await ProductServ.single(productId);
      const fullProduct = res?.data || res;
      setSelectedProductForDetails(fullProduct);
    } catch (error) {
      console.error("Error fetching product details:", error);
      message.error("Failed to load full product details");
    } finally {
      setProductDetailsLoading(false);
    }
  };

  const [brands, setBrands] = useState([]);
  const [totalBrand, setTotalBrand] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");

  const fetchBrands = useCallback(async (p, sText, size) => {
    try {
      setLoading(true);
      const filterObj = {};
      if (sText) filterObj.name = { $regex: sText, $options: "i" };
      
      const apiParams = {
        limit: size,
        skip: (p - 1) * size,
        sort: JSON.stringify({ updatedAt: -1 }),
        filter: JSON.stringify(filterObj),
      };
      const response = await BrandsServ.index(apiParams);
      
      let brandsData = [];
      if (Array.isArray(response)) brandsData = response;
      else if (response && Array.isArray(response.data)) brandsData = response.data;
      else if (response && Array.isArray(response.brands)) brandsData = response.brands;
      else if (response && Array.isArray(response.items)) brandsData = response.items;

      const normalizedBrands = brandsData.map((brand) => ({ ...brand, id: brand.id || brand._id }));
      setBrands(normalizedBrands);
      setTotalBrand(response?.total || normalizedBrands.length || 0);
    } catch (error) {
      console.error("Error fetching brands:", error);
      message.error("Failed to load brands");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => { 
      fetchBrands(page, searchText, pageSize); 
    }, 300);
    return () => clearTimeout(handler);
  }, [page, searchText, pageSize, fetchBrands]);

  const handleSearch = useCallback((value) => {
    setSearchText(value);
    setPage(1);
  }, []);

  const refresh = useCallback(() => {
    fetchBrands(page, searchText, pageSize);
  }, [fetchBrands, page, searchText, pageSize]);

  const handleModalClose = (isRefresh) => { setAddBrands(false); if (isRefresh) refresh(); };

  const handleAddBrand = () => {
    setBrandDetails(null);
    setIsEditing(false);
    setAddBrands(true);
  };

  const handleEdit = (record) => {
    setBrandDetails(record);
    setIsEditing(true);
    setAddBrands(true);
    setSelectedRecord(null);
  };

  const handleDelete = async (record) => {
    const brandIdToDelete = record._id || record.id;
    try {
      await BrandsServ.remove(brandIdToDelete);
      message.success("Brand deleted successfully");
      setSelectedRecord(null);
      setConfirmDelete(false);
      refresh();
    } catch (error) {
      console.error("Error deleting brand:", error);
      message.error(error.message || "Failed to remove brand");
    }
  };

  const refreshBrands = async (isRefresh) => {
    setAddBrands(false);
    if (isRefresh) refresh(true);
  };

  const filteredData = brands.filter((item) =>
    item.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const viewBrandProducts = (record) => {
    setSelectedBrandName(record.name);
    setSelectedBrandProducts(record.products || []);
    setProductDrawerOpen(true);
    setSelectedRecord(null);
  };

  /* ── Table Columns ── */
  const columns = [
    {
      title: "Image",
      dataIndex: "imageUrl",
      key: "logo",
      width: 70,
      align: "center",
      render: (text) => <BrandTableImage src={text} alt="Brand Logo" />,
    },
    {
      title: "Brand Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div>
          <span style={{ fontWeight: 700, color: "#1e293b", fontSize: 14 }}>{name}</span>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
            ID: {(record._id || record.id)?.toString().slice(-10).toUpperCase()}
          </div>
        </div>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (desc) => (
        <span style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>
          {desc ? (desc.length > 60 ? desc.slice(0, 60) + "…" : desc) : "—"}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      align: "center",
      render: (_, record) => {
        const isActive = record.isActive !== false;
        return (
          <span style={{
            display: "inline-flex", alignItems: "center", padding: "4px 14px",
            borderRadius: 20, fontSize: 11, fontWeight: 800, letterSpacing: "0.5px",
            textTransform: "uppercase",
            background: isActive ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)",
            border: `1.5px solid ${isActive ? "#86efac" : "#fca5a5"}`,
            color: isActive ? "#16a34a" : "#dc2626",
          }}>
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      render: (date) => (
        <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          {formatDate(date)}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => { setSelectedRecord(record); setDescExpanded(false); setConfirmDelete(false); }}
          style={{
            borderRadius: 3,
            fontWeight: 600,
            background: '#ffffffff',
            color: '#004754ff',
            border: '1px solid #e4e4e7',
            boxShadow: '0 0 0 1px #e4e4e7',
            height: 'auto',
            padding: '0px 8px',
            fontSize: 13,
          }}
        >
          View
        </Button>
      ),
    },
  ];

  let header = "Brands";
  const topRef = React.useRef(null);

  return (
    <div className="brand-page pcu-padding-24" ref={topRef}>
      <div className="pcu-margin-bottom-16">
        <Breadcrumbs />
      </div>

      <div className="brands-container" style={{ padding: "24px", background: "white", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        {/* Header Section */}
        <div className="brands-header" style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <div
              style={{
                width: "48px", height: "48px", background: "#fff",
                borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)", border: "1px solid #f1f5f9",
              }}
            >
              <IdcardOutlined style={{ fontSize: "22px", color: "#4776E6" }} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 800, color: "#1a1d2e", letterSpacing: "-0.5px" }}>Product Brands</h1>
              <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af", fontWeight: 500 }}>{totalBrand} brands registered in system</p>
            </div>
          </div>
          <Button
            type="primary"
            onClick={handleAddBrand}
            style={{
              height: "40px", borderRadius: "10px",
              background: "linear-gradient(135deg, #FF8800 0%, #FF5E00 100%)",
              border: "none", padding: "0 20px", fontSize: "13px", fontWeight: 700,
              boxShadow: "0 4px 12px rgba(255, 136, 0, 0.3)",
              display: "flex", alignItems: "center", gap: "8px",
            }}
          >
            <PlusOutlined style={{ fontSize: "16px" }} />
            Add Brand
          </Button>
        </div>

        {/* Filters */}
        <div className="filter-section" style={{ marginBottom: "24px", display: "grid", gridTemplateColumns: "1fr auto", gap: "12px", alignItems: "flex-end" }}>
          <div style={{ maxWidth: "400px" }}>
            <span style={{ color: "#94a3b8", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>Search Brands</span>
            <div style={{ position: "relative" }}>
              <SearchOutlined style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", zIndex: 1 }} />
              <Input
                placeholder="Search by brand name..."
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ height: "40px", paddingLeft: "36px", borderRadius: "10px", border: "1px solid #e2e8f0" }}
              />
            </div>
          </div>
          <ResetButton onClick={() => handleSearch("")} text="Reset Filters" />
        </div>

        <div className="pcu-border-top pcu-margin-x-n24">
          <DataTable
            data={filteredData}
            columns={columns}
            rowKey="id"
            locale={{
              emptyText: (
                <EmptyState
                  title="No Brands Found"
                  description={searchText ? "No brands match your search." : "No brands found. Add one to get started."}
                  gradient="blue"
                  onAction={!searchText ? handleAddBrand : undefined}
                  actionText="Add Brand"
                  icon="inbox"
                />
              )
            }}
            loading={loading}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: totalBrand,
              onShowSizeChange: (c, s) => { setPageSize(s); setPage(1); },
              onChange: (p) => {
                setPage(p);
                topRef.current?.scrollIntoView({ behavior: 'smooth' });
              },
            }}
          />
        </div>

        {/* ── Details Modal (Shop-style) ── */}
        <Modal
          open={!!selectedRecord}
          onCancel={() => setSelectedRecord(null)}
          footer={null}
          width={580}
          centered
          className="premium-modal"
          closeIcon={
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          }
        >
          {selectedRecord && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* ── Hero: Image + Name + Status ── */}
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ width: 72, height: 72, borderRadius: 14, overflow: "hidden", border: "1.5px solid #e0e7ff", flexShrink: 0 }}>
                  <SafeImage
                    src={selectedRecord.imageUrl}
                    alt={selectedRecord.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: "#111827" }}>{selectedRecord.name}</h2>
                    <Tag
                      color={selectedRecord.isActive !== false ? "success" : "error"}
                      style={{ borderRadius: 6, fontWeight: 700, fontSize: 10, padding: "1px 8px" }}
                    >
                      {selectedRecord.isActive !== false ? "ACTIVE" : "INACTIVE"}
                    </Tag>
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                    ID: {(selectedRecord._id || selectedRecord.id)?.toString().slice(-14).toUpperCase()}
                  </div>
                </div>
              </div>

              {/* ── Detail Grid ── */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {/* Description — full width, scrollable */}
                <div style={{ gridColumn: "1 / -1", background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Description</div>
                  <div style={{
                    fontSize: 13, color: "#374151", fontWeight: 500, lineHeight: 1.6,
                    maxHeight: descExpanded ? 180 : 88,
                    overflowY: descExpanded ? "auto" : "hidden",
                    transition: "max-height 0.25s ease",
                    paddingRight: descExpanded ? 4 : 0,
                  }}>
                    {selectedRecord.description || "No description provided."}
                  </div>
                  {selectedRecord.description && selectedRecord.description.length > 120 && (
                    <button
                      onClick={() => setDescExpanded(p => !p)}
                      style={{
                        marginTop: 6, background: "none", border: "none", padding: 0,
                        cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#4776E6",
                        display: "flex", alignItems: "center", gap: 4,
                      }}
                    >
                      {descExpanded ? "▲ Show less" : "▼ Show more"}
                    </button>
                  )}
                </div>

                {/* Status */}
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Status</div>
                  <span style={{
                    display: "inline-flex", alignItems: "center", padding: "4px 12px",
                    borderRadius: 20, fontSize: 11, fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase",
                    background: selectedRecord.isActive !== false ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.08)",
                    border: `1.5px solid ${selectedRecord.isActive !== false ? "#86efac" : "#fca5a5"}`,
                    color: selectedRecord.isActive !== false ? "#16a34a" : "#dc2626",
                  }}>
                    {selectedRecord.isActive !== false ? "Active" : "Inactive"}
                  </span>
                </div>

                {/* Products */}
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Products</div>
                  <button
                    onClick={() => viewBrandProducts(selectedRecord)}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      background: "rgba(71,118,230,0.08)", border: "1.5px solid #c7d2fe",
                      borderRadius: 20, padding: "3px 14px", cursor: "pointer",
                      color: "#4776E6", fontWeight: 700, fontSize: 13,
                    }}
                  >
                    <ShoppingOutlined style={{ fontSize: 13 }} />
                    {selectedRecord.products?.length || 0} Products
                  </button>
                </div>

                {/* Created At */}
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Created At</div>
                  <div style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>{formatDate(selectedRecord.createdAt)}</div>
                </div>

                {/* Updated At */}
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Updated At</div>
                  <div style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>{formatDate(selectedRecord.updatedAt)}</div>
                </div>
              </div>

              {/* ── Footer Actions ── */}
              <div style={{ paddingTop: 14, borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", gap: 10 }}>
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(selectedRecord)}
                    style={{ height: 36, borderRadius: 8, color: "#4776E6", border: "1px solid #c7d2fe", background: "#eef2ff", fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 4 }}
                  >
                    Edit
                  </Button>
                  {!confirmDelete ? (
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => setConfirmDelete(true)}
                      disabled={selectedRecord.products?.length > 0}
                      style={{ height: 36, borderRadius: 8, color: "#dc2626", border: "1px solid #fca5a5", background: "#fef2f2", fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 4 }}
                    > Remove </Button>
                  ) : (
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>Confirm?</span>
                      <Button
                        size="small"
                        danger
                        onClick={() => handleDelete(selectedRecord)}
                        style={{ borderRadius: 6, fontWeight: 700, fontSize: 11, height: 28 }}
                      >
                        Yes
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setConfirmDelete(false)}
                        style={{ borderRadius: 6, fontWeight: 700, fontSize: 11, height: 28 }}
                      >
                        No
                      </Button>
                    </div>
                  )}
                </div>
                <Button
                  onClick={() => setSelectedRecord(null)}
                  style={{ height: 36, borderRadius: 8, fontWeight: 700, fontSize: 13 }}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Drawer for Products */}
        <Drawer
          title={`Products in ${selectedBrandName}`}
          placement="right"
          width={500}
          open={productDrawerOpen}
          onClose={() => setProductDrawerOpen(false)}
        >
          {selectedBrandProducts.length === 0 ? (
            <div style={{ padding: 20, display: 'flex', justifyContent: 'center' }}>
              <EmptyState
                title="No Products"
                description="No products found for this brand."
                gradient="purple"
                showAction={false}
                icon="inbox"
                size="small"
              />
            </div>
          ) : (
            <div style={{ padding: 10 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                {selectedBrandProducts.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => handleProductCardClick(product)}
                    style={{
                      border: "1px solid #eee",
                      borderRadius: 12,
                      padding: 10,
                      boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                    }}
                    className="drawer-product-card"
                  >
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      style={{
                        width: "100%", height: 120, objectFit: "cover",
                        borderRadius: 10, marginBottom: 10,
                      }}
                    />
                    <Typography.Text strong style={{ fontSize: 14 }}>
                      {product.name}
                    </Typography.Text>
                    <br />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Stock: {product.stock}
                    </Typography.Text>
                    <br />
                    <Typography.Text type="success" style={{ fontSize: 13 }}>
                      ₹ {product.price}
                    </Typography.Text>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Drawer>

        {/* Premium Compact Product Details Modal (Admin Version) */}
        <Modal
          open={!!selectedProductForDetails}
          onCancel={() => setSelectedProductForDetails(null)}
          footer={null}
          width={680}
          closable={true}
          className="ss-modal"
          centered
        >
          {productDetailsLoading && !selectedProductForDetails?.name && (
            <div style={{ padding: '30px', textAlign: 'center' }}>
              <Spin size="large" tip="Loading details..." />
            </div>
          )}
          {selectedProductForDetails && (
            <div className="ss-modal-body" style={{ position: 'relative' }}>
              {productDetailsLoading && (
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(255,255,255,0.4)', zIndex: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 16, backdropFilter: 'blur(1px)'
                }}>
                  <Spin size="large" />
                </div>
              )}
              <div className="ss-modal-hero" style={{ position: 'relative', padding: '12px 20px', gap: 16, display: 'flex', alignItems: 'center' }}>
                <div className="ss-modal-img" style={{ border: '1.5px solid #e0e7ff', width: 70, height: 70, borderRadius: 12, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {(!selectedProductForDetails.imageUrl || detailImageError) ? (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <ShoppingOutlined style={{ fontSize: 32, color: '#94a3b8' }} />
                    </div>
                  ) : (
                    <Image
                      src={getImageUrl(selectedProductForDetails.imageUrl)}
                      alt={selectedProductForDetails.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      preview={{ mask: null }}
                      onError={() => setDetailImageError(true)}
                    />
                  )}
                </div>
                <div className="ss-modal-info" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <h2 className="ss-modal-name" style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#111827' }}>{selectedProductForDetails.name}</h2>
                    {(() => {
                      const status = selectedProductForDetails.approvalStatus || 'pending';
                      const map = {
                        approved: { color: '#10b981', bg: '#ecfdf5', text: 'Approved' },
                        rejected: { color: '#ef4444', bg: '#fef2f2', text: 'Rejected' },
                        pending: { color: '#ffffff', bg: '#dc2626', text: 'Pending' }
                      };
                      const config = map[status] || map.pending;
                      return (
                        <span style={{
                          padding: '1px 6px',
                          borderRadius: '4px',
                          fontSize: '9px',
                          fontWeight: 700,
                          backgroundColor: config.bg,
                          color: config.color,
                          textTransform: 'uppercase',
                          border: `1px solid ${config.color}20`
                        }}>
                          {config.text}
                        </span>
                      );
                    })()}
                  </div>
                  <div style={{ fontSize: '10px', color: '#9ca3af', fontWeight: 500 }}>
                    ID: {selectedProductForDetails._id?.substring(0, 12).toUpperCase() || selectedProductForDetails.id?.substring(0, 12).toUpperCase()} • {selectedProductForDetails.categoryId?.name || selectedProductForDetails.category || "General"}
                  </div>
                </div>
              </div>

              {selectedProductForDetails.approvalStatus === 'rejected' && selectedProductForDetails.rejectionReason && (
                <div style={{
                  margin: '12px 20px 12px 20px',
                  padding: '6px 10px',
                  background: '#fff1f0',
                  border: '1px solid #ffa39e',
                  borderRadius: 8,
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center'
                }}>
                  <InfoCircleOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />
                  <div style={{ fontSize: '11px', color: '#cf1322', fontWeight: 600 }}>
                    <span style={{ opacity: 0.6, marginRight: 4, fontSize: '9px', textTransform: 'uppercase' }}>Reason:</span>
                    {selectedProductForDetails.rejectionReason}
                  </div>
                </div>
              )}

              {selectedProductForDetails.approvalStatus === 'approved' && selectedProductForDetails.approvalNote && (
                <div style={{
                  margin: '12px 20px 12px 20px',
                  padding: '6px 10px',
                  background: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: 8,
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center'
                }}>
                  <InfoCircleOutlined style={{ color: '#10b981', fontSize: 12 }} />
                  <div style={{ fontSize: '11px', color: '#15803d', fontWeight: 600 }}>
                    <span style={{ opacity: 0.7, marginRight: 4, fontSize: '9px', textTransform: 'uppercase' }}>Approval Note:</span>
                    {selectedProductForDetails.approvalNote}
                  </div>
                </div>
              )}

              <div style={{ padding: '12px 20px 20px' }}>
                {/* Detailed Pricing Grid - 4 Columns */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '12px',
                  background: '#fff',
                  padding: '12px',
                  borderRadius: 12,
                  border: '1.5px solid #f1f5f9',
                  marginBottom: 16,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                }}>
                  <div>
                    <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>MRP Price</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#64748b', textDecoration: 'line-through' }}>{formatPrice(selectedProductForDetails.mrp || selectedProductForDetails.price)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>Seller Price</div>
                    <div style={{ fontSize: '14px', fontWeight: 800, color: '#0ea5e9' }}> {formatPrice(
                      selectedProductForDetails?.variants?.[0]?.sellerPrice ||
                      selectedProductForDetails?.sellerPrice
                    )}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>Buyer Price</div>
                    <div style={{ fontSize: '14px', fontWeight: 800, color: '#f97316' }}>{formatPrice(selectedProductForDetails.buyerPrice || selectedProductForDetails.price)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>Admin Markup</div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#10b981' }}>
                        {selectedProductForDetails.adminMarkupType === 'percentage' ? `${selectedProductForDetails.adminMarkupValue}%` :
                          selectedProductForDetails.adminMarkupType === 'fixed' ? `₹${selectedProductForDetails.adminMarkupValue}` :
                            `${selectedProductForDetails.categoryCommissionPercentage || 0}%`}
                      </span>
                      <span style={{ fontSize: '10px', color: '#10b981', opacity: 0.8 }}>
                        {formatPrice(selectedProductForDetails.markupAmount || (selectedProductForDetails.price - (selectedProductForDetails.sellerPrice || selectedProductForDetails.price)))}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Stock & Brand Info */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '12px',
                  background: '#f8fafc',
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '1px solid #e2e8f0',
                  marginBottom: 12
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 6, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0' }}>
                      <AppstoreOutlined style={{ color: '#4776E6', fontSize: 14 }} />
                    </div>
                    <div>
                      <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Stock Status</div>
                      <div style={{ fontSize: '12px', fontWeight: 700, color: selectedProductForDetails.stock > 0 ? '#10b981' : '#ef4444' }}>
                        {selectedProductForDetails.stock > 0 ? `${selectedProductForDetails.stock} In Stock` : 'Out of Stock'}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 6, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0' }}>
                      <TagOutlined style={{ color: '#4776E6', fontSize: 14 }} />
                    </div>
                    <div>
                      <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Brand</div>
                      <div style={{ fontSize: '12px', fontWeight: 700, color: '#475569' }}>
                        {selectedProductForDetails.brandId?.name || selectedProductForDetails.brandName || selectedBrandName || "N/A"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gallery Images */}
                {selectedProductForDetails.images && selectedProductForDetails.images.length > 0 && (
                  <div style={{ marginBottom: 15 }}>
                    <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Gallery Images</div>
                    <div style={{
                      display: 'flex',
                      gap: 10,
                      overflowX: 'auto',
                      paddingBottom: 8,
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none'
                    }}>
                      {selectedProductForDetails.images.map((img, idx) => (
                        <div key={idx} style={{
                          minWidth: 80,
                          width: 80,
                          height: 80,
                          borderRadius: 10,
                          border: '1.5px solid #f1f5f9',
                          overflow: 'hidden',
                          flexShrink: 0,
                          backgroundColor: '#fff',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                        }}>
                          <Image
                            src={getImageUrl(img)}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            alt={`Gallery ${idx}`}
                            fallback="https://via.placeholder.com/80?text=Error"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Description</div>
                  <div style={{ fontSize: '12px', color: '#475569', lineHeight: '1.4' }}>
                    {selectedProductForDetails.description || "No description provided."}
                  </div>
                </div>

                {/* Options/Variants */}
                {((selectedProductForDetails.options && selectedProductForDetails.options.length > 0) || (selectedProductForDetails.variants && selectedProductForDetails.variants.length > 0)) && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Available Options</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {selectedProductForDetails.options?.map((opt, oIdx) => (
                        <div key={oIdx}>
                          {opt.variants?.map((v, vIdx) => (
                            <div key={vIdx} style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              background: '#fff',
                              border: '1px solid #f1f5f9',
                              borderRadius: 8,
                              marginBottom: 4,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.01)'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {v.imageUrl && (
                                  <Image
                                    src={getImageUrl(v.imageUrl)}
                                    style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover', border: '1.5px solid #f1f5f9' }}
                                    alt="Variant"
                                    preview={{ mask: null }}
                                  />
                                )}
                                <div>
                                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b' }}>{v.value}</div>
                                  <div style={{ fontSize: '9px', color: '#94a3b8', textDecoration: 'line-through' }}>MRP: {formatPrice(v.mrp || v.price)}</div>
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontSize: '8px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Seller</div>
                                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#0ea5e9' }}>{formatPrice(v.sellerPrice || v.price)}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontSize: '8px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Buyer</div>
                                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#f97316' }}>{formatPrice(v.price)}</div>
                                </div>
                                <div style={{ textAlign: 'right', minWidth: 60 }}>
                                  <div style={{ fontSize: '8px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Stock</div>
                                  <span style={{ fontSize: '11px', fontWeight: 700, color: v.stock > 0 ? '#10b981' : '#ef4444' }}>
                                    {v.stock > 0 ? `${v.stock} pcs` : 'Out'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}

                      {/* If no new options but legacy variants exist */}
                      {(!selectedProductForDetails.options || selectedProductForDetails.options.length === 0) && selectedProductForDetails.variants?.map((v, vIdx) => (
                        <div key={vIdx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          background: '#fff',
                          border: '1px solid #f1f5f9',
                          borderRadius: 8,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.01)'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {v.imageUrl && (
                              <Image
                                src={getImageUrl(v.imageUrl)}
                                style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover', border: '1.5px solid #f1f5f9' }}
                                alt="Variant"
                                preview={{ mask: null }}
                              />
                            )}
                            <div>
                              <div style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b' }}>{v.value}</div>
                              <div style={{ fontSize: '9px', color: '#94a3b8', textDecoration: 'line-through' }}>MRP: {formatPrice(v.mrp || v.price)}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '8px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Seller</div>
                              <div style={{ fontSize: '11px', fontWeight: 700, color: '#0ea5e9' }}>{formatPrice(v.sellerPrice || v.price)}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '8px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Buyer</div>
                              <div style={{ fontSize: '11px', fontWeight: 700, color: '#f97316' }}>{formatPrice(v.price)}</div>
                            </div>
                            <div style={{ textAlign: 'right', minWidth: 60 }}>
                              <div style={{ fontSize: '8px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Stock</div>
                              <span style={{ fontSize: '11px', fontWeight: 700, color: v.stock > 0 ? '#10b981' : '#ef4444' }}>
                                {v.stock > 0 ? `${v.stock} pcs` : 'Out'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal>

        {/* Add/Edit Brand Modal */}
        {addBrands && (
          <AddBrands
            open={addBrands}
            onClose={handleModalClose}
            brandDetails={brandDetails}
            isEditing={isEditing}
            refreshBrands={handleModalClose}
          />
        )}
      </div>
    </div>
  );
};

export default ProductBrands;
